import { Box, Button, DialogBody, DialogFooter, Flex, HStack, Image, Separator, Spinner, Stack, Text, useDisclosure } from "@chakra-ui/react"
import { Form, UI } from "components"
import { toaster, API, Mask } from "config"
import useOnboardingConsultant from "hooks/useOnboardingConsultant"
import { useState } from "react"
import { formatPhoneNumber, formatPhoneNumberIntl } from "react-phone-number-input"
import { useParams, useNavigate } from "react-router-dom"
import {useSearchParams} from "react-router";
import confetti from "canvas-confetti"

export default function Onboarding() {
    const params = useParams()
    const consultant = useOnboardingConsultant(params.id)

    if (consultant.isError) return (
        <Box maxW="500px" margin="0 auto" py={8} px={4}>
            <Stack gap={4}>
                <Box w="100%" textAlign="center"><Image src="/icon.png" h="70px" /></Box>
                <Separator />
                <UI.Alert size="sm" status="error">O consultor informado não foi encontrado. Por favor, verifique o link de indicação e tente novamente.</UI.Alert>
            </Stack>
        </Box>
    )

    if (consultant.isFetching) return (
        <Box maxW="500px" margin="0 auto" py={8} px={4}>
            <Stack gap={4}>
                <Box w="100%" textAlign="center"><Image src="/icon.png" h="70px" /></Box>
                <Separator />
                <UI.Alert size="sm" indicator={<Spinner />}>Carregando informações do seu representante...</UI.Alert>
            </Stack>
        </Box>
    )

    return (
        <Box maxW="500px" margin="0 auto" py={8} px={4}>
            <Stack gap={4}>
                <Flex w="100%" justifyContent="space-between" align="center">
                    <Image src="/icon.png" h="70px" />
                    <HStack gap={3}>
                        <UI.Avatar size="xs" src={consultant.data?.customer.avatar_url} name={consultant.data?.customer.name} />
                        <Box>
                            <Text fontSize="xs" color="GrayText" lineHeight="1">Indicado por:</Text>
                            <Text fontSize="sm" fontWeight="bold">{consultant.data?.customer.name}</Text>
                        </Box>
                    </HStack>
                </Flex>
                <Separator />
                <Register />
            </Stack>
        </Box>
    )
}

const Register = () => {
    const navigate = useNavigate()
    const params = useParams()
    const termsModal = useDisclosure()
    const consultant = useOnboardingConsultant(params.id)
    const [searchParams] = useSearchParams()
    const refs = searchParams.get('ref')

    const [loading, setLoading] = useState(false)
    const [errors, setErrors] = useState(false)
    const [form, setForm] = useState({ doc_type: 1 })

    const isFormValid =
        form.name?.length >= 6 &&
        form.dob?.length === 10 &&
        (form.doc_type == 1 ? form.doc_cpf?.length === 14 : form.doc_international?.length >= 3) &&
        form.email?.includes('@') &&
        form.mobile?.length >= 10 &&
        form.password?.length >= 6 &&
        form.password === form.password_retype;

    const handleChange = ({ target }) => {
        const { name, value } = target
        switch (name) {
            default: setForm({ ...form, [name]: value }); break;
            case 'dob': setForm({ ...form, [name]: Mask.date(value) }); break;
            case 'doc_cpf': setForm({ ...form, [name]: Mask.cpf(value) }); break;
        }
    }

    const startCelebration = () => {
        const duration = 5 * 1000; // 5 segundos de festa
        const animationEnd = Date.now() + duration;
        const defaults = {
            startVelocity: 30,
            spread: 360,
            ticks: 60,
            zIndex: 10000,
            scalar: 1.5,
            colors: ['#FF4500', '#00FF00', '#FF00FF', '#00FFFF', '#FFD700', '#FF1493']
        };

        const randomInRange = (min, max) => Math.random() * (max - min) + min;

        const interval = setInterval(function() {
            const timeLeft = animationEnd - Date.now();

            if (timeLeft <= 0) {
                return clearInterval(interval);
            }

            const particleCount = 50 * (timeLeft / duration);

            // Disparos laterais aleatórios
            confetti({
                ...defaults,
                particleCount,
                origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
            });
            confetti({
                ...defaults,
                particleCount,
                origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
            });
        }, 250);
    }

    const handleFinalSubmit = async () => {
        setLoading(true)
        try {
            // Inicia a sequência de fogos/confetes
            startCelebration();

            const payload = {
                ...form,
                mobile: formatPhoneNumber(form.mobile),
                mobile_country_code: formatPhoneNumberIntl(form.mobile).split(' ')[0],
                consultant_upline_id: consultant.data?.id,
                options: {
                    terms: {
                        accept_terms: true,
                        date_accepted: new Date().toISOString(),
                    }
                }
            };

            if (refs) {
                payload.bonihub_reference_code = refs;
            }

            await API.post('/register', payload);

            const login = await API.post('/auth', { email: form.email, password: form.password })
            localStorage.removeItem('REACT_QUERY_OFFLINE_CACHE')
            localStorage.setItem('@token', login.data?.plainTextToken)

            toaster.create({ title: 'Bem vindo à Bonífica!', type: 'success' })

            termsModal.onClose()

            // Aumentei para 3 segundos para dar tempo de ver a animação contínua
            setTimeout(() => {
                navigate('/adesao')
                window.location.reload()
            }, 3500)

        } catch (err) {
            setErrors(err?.response?.data?.errors)
            toaster.create({
                title: 'Atenção!',
                description: err?.response?.data?.message || 'Erro ao processar cadastro.',
                type: 'warning'
            })
            setLoading(false)
            if (err?.response?.status === 400 || err?.response?.status === 422) {
                termsModal.onClose()
            }
        }
    }

    return (
        <>
            <Text fontSize="sm" color="GrayText">
                Seja bem vindo (a) a Bonífica! Nosso consultor parceiro <Text as="span" color="orange.fg" fontWeight="bold">{consultant.data?.customer?.name}</Text> te indicou. Vamos começar?
            </Text>

            <Form.Container autocomplete="off" onSubmit={(e) => e.preventDefault()}>
                <Text color="GrayText" fontSize="xs" textTransform="uppercase" fontWeight="bold">Dados pessoais</Text>
                <Form.Text
                    required
                    label="Nome completo:"
                    name="name"
                    value={form?.name}
                    error={errors?.name}
                    onChange={handleChange}
                    disabled={loading}
                    placeholder="Ex.: Maria dos Santos"
                    helper="Conforme consta no seu documento oficial."
                    fontWeight="bold"
                    size="lg"
                    variant="filled"
                />
                <Form.Text
                    required
                    label="Data de nascimento:"
                    name="dob"
                    value={form?.dob}
                    error={errors?.dob}
                    onChange={handleChange}
                    disabled={loading}
                    placeholder="Ex.: 01/10/1990"
                    variant="filled"
                />
                <Separator />
                <Form.Segment
                    required
                    label="Tipo de documento:"
                    name="doc_type"
                    value={form?.doc_type}
                    onChange={handleChange}
                    items={[{ label: '🇧🇷 CPF', value: '1' }, { label: '🌐 Internacional', value: '3' }]}
                />
                <UI.Show visible={form?.doc_type == 1}>
                    <Form.Text
                        required
                        label="CPF:"
                        name="doc_cpf"
                        value={form?.doc_cpf}
                        error={errors?.doc_cpf}
                        onChange={handleChange}
                        disabled={loading}
                        placeholder="Ex.: 999.999.999-99"
                        helper="Número do seu cadastro de pessoa física brasileiro."
                        size="lg"
                        variant="filled"
                    />
                </UI.Show>
                <UI.Show visible={form?.doc_type == 3}>
                    <Form.Text
                        required
                        label="Número do documento:"
                        name="doc_international"
                        value={form?.doc_international}
                        error={errors?.doc_international}
                        onChange={handleChange}
                        disabled={loading}
                        helper="O número do documento oficial do seu país."
                        variant="filled"
                    />
                </UI.Show>
                <Separator />
                <Text color="GrayText" fontSize="xs" textTransform="uppercase" fontWeight="bold">Dados de contato</Text>
                <Form.Text
                    required
                    type="email"
                    label="Endereço de e-mail:"
                    name="email"
                    value={form?.email}
                    error={errors?.email}
                    onChange={handleChange}
                    disabled={loading}
                    placeholder="Ex.: maria@gmail.com"
                    helper="Este será o login de acesso ao seu escritório."
                    fontWeight="bold"
                    size="lg"
                    variant="filled"
                />
                <Form.Mobile
                    required
                    label="Celular/Whatsapp"
                    name="mobile"
                    value={form?.mobile}
                    error={errors?.mobile}
                    onChange={(val) => setForm(prev => ({ ...prev, mobile: val }))}
                    disabled={loading}
                    placeholder="Ex.: (99) 9 9999 9999"
                    variant="filled"
                />
                <Separator />
                <Text color="GrayText" fontSize="xs" textTransform="uppercase" fontWeight="bold">Segurança</Text>
                <HStack align="flex-start">
                    <Form.Password
                        required
                        label="Escolha uma senha:"
                        name="password"
                        value={form?.password}
                        error={errors?.password}
                        onChange={handleChange}
                        disabled={loading}
                        variant="filled"
                        w="100%"
                    />
                    <Form.Password
                        required
                        label="Repita a senha:"
                        name="password_retype"
                        value={form?.password_retype}
                        error={errors?.password_retype}
                        onChange={handleChange}
                        disabled={loading}
                        variant="filled"
                    />
                </HStack>

                <Box p={4} mt={4} borderWidth={1} borderRadius={4} borderStyle="dashed" borderColor="gray.300" bg="gray.50">
                    <Text fontSize="sm" color="gray.600" mb={3}>
                        Para finalizar o seu cadastro, leia e aceite os termos de utilização abaixo.
                    </Text>
                    <Button
                        variant={isFormValid ? "subtle" : "outline"}
                        colorPalette={isFormValid ? "orange" : "gray"}
                        width="full"
                        onClick={termsModal.onOpen}
                        size="xl"
                        disabled={!isFormValid || loading}
                        loading={loading}
                        fontWeight="bold"
                        _disabled={{
                            bg: "gray.100",
                            color: "gray.500",
                            borderColor: "gray.300",
                            cursor: "not-allowed",
                            opacity: 1
                        }}
                    >
                        {isFormValid ? "Ler Termos e Finalizar" : "Preencha os dados acima"}
                    </Button>
                </Box>

                <DialogTerms
                    open={termsModal.open}
                    onClose={termsModal.onClose}
                    onAccept={handleFinalSubmit}
                    loading={loading}
                />
            </Form.Container>
        </>
    )
}

const DialogTerms = ({ open, onClose, onAccept, loading }) => {
    return (
        <UI.Dialog title="Termos de Utilização" open={open} onClose={onClose} size="lg">
            <DialogBody>
                <Stack gap={4}>
                    <Text fontSize="sm">
                        Ao confirmar, você declara que leu e concorda com os <b>Termos de Utilização</b> da Bonífica.
                    </Text>
                    <Separator />
                    <Box maxH="40vh" overflowY="auto" p={4} bg="gray.950" color="white" borderRadius="md">
                        <UI.Terms />
                    </Box>
                </Stack>
            </DialogBody>
            <DialogFooter borderTopWidth="1px" pt={4}>
                <Button
                    colorPalette="green"
                    loading={loading}
                    onClick={onAccept}
                    width="full"
                    size="lg"
                >
                    <UI.Icon name="check" mr={2} /> Aceitar Termos e Criar Conta
                </Button>
            </DialogFooter>
        </UI.Dialog>
    )
}